import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo } from "react";
import {
  addToCart,
  removeFromCart,
  updateQty,
  clearCart,
  CartItem,
} from "../store/cartSlice";
import { RootState } from "../store/store";

/**
 * Custom hook for cart management using Redux
 * Returns cart items and methods to manipulate cart
 */
export const useCart = () => {
  const dispatch = useDispatch();

  // Select cart items from Redux store
  const items = useSelector((state: any) => {
    const cartItems = state?.cart?.items as CartItem[];
    return cartItems || [];
  });

  // Memoized selectors for performance
  const cartCount = useMemo(
    () => items.reduce((sum: number, item: CartItem) => sum + item.qty, 0),
    [items]
  );

  // Number of items in cart (not total qty)
  const itemCount = useMemo(() => items.length, [items]);

  const cartTotal = useMemo(
    () => items.reduce((sum: number, item: CartItem) => sum + item.qty, 0),
    [items]
  );

  // Check if product is in cart
  const isInCart = useCallback(
    (productId: string): boolean => {
      return items.some((item: CartItem) => item.productId === productId);
    },
    [items]
  );

  // Get quantity of a product in cart
  const getQty = useCallback(
    (productId: string): number => {
      const item = items.find((item: CartItem) => item.productId === productId);
      return item?.qty ?? 0;
    },
    [items]
  );

  // Add to cart
  const handleAddToCart = useCallback(
    (productId: string, qty: number = 1) => {
      dispatch(addToCart({ productId, qty }));
    },
    [dispatch]
  );

  // Remove from cart
  const handleRemoveFromCart = useCallback(
    (productId: string) => {
      dispatch(removeFromCart(productId));
    },
    [dispatch]
  );

  // Update quantity
  const handleUpdateQty = useCallback(
    (productId: string, qty: number) => {
      dispatch(updateQty({ productId, qty }));
    },
    [dispatch]
  );

  // Clear entire cart
  const handleClearCart = useCallback(() => {
    dispatch(clearCart());
  }, [dispatch]);

  return {
    cart: items,
    items,
    cartCount,
    itemCount,
    cartTotal,
    isInCart,
    getQty,
    addToCart: handleAddToCart,
    removeFromCart: handleRemoveFromCart,
    updateQty: handleUpdateQty,
    clearCart: handleClearCart,
  };
};
